/**
 * ComposedDocumentBody — COMPOSED 보고서 본문의 공용 렌더 코어.
 *
 * 상세(ComposerReportDetail)·공유(`/share/:token`)·외부결재(`/approval/:token`)가 **동일 컴포넌트**를
 * 재사용해 파리티를 구조적으로 보장한다(신규 렌더러 금지 — 세 surface가 같은 rc 분할 엔진/결재란 special).
 *
 * 렌더: templateSnapshot(생성 시 박제)이 있으면 그것으로, 없으면 templateKey로 RenderTemplate을 live
 *   로드해 본문을 정적 렌더한다. 스냅샷 우선은 무결성 보장 — 확정 후 템플릿이 수정돼도 과거 보고서는
 *   박제된 양식 그대로 렌더된다(법적 증빙).
 *   - data = { ...resolveForRender(template, content, _, liveData), approval } — body 노드가
 *     bindingKey로 읽는 데이터 소스 + 결재란 special(approvalBlock) 입력.
 *
 * live 데이터: templateSnapshot/templateKey만으로 그릴 수 있으나, live lifecycle 바인딩(현재값)이 있는
 *   상세는 reportId를 넘겨 내부에서 fetch한다. 공유/외부결재는 무인증 경로(Puppeteer 쿠키 부재)라
 *   reportId를 넘기지 않으며, live 바인딩은 종전대로 undefined로 렌더된다(스냅샷 content가 정본).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ApprovalBlockSpecial,
  pagePx,
  resolveForRender,
} from '@safetyhub/report-composer';
import type {
  ApprovalBlockData,
  ReportTemplate,
  RenderData,
  SpecialComponentMap,
  ComposerNode,
} from '@safetyhub/report-composer';
import { templateHasSignatureSlots } from '@safetyhub/shared';
import { PagedComposerView } from './PagedComposerView';
import { ComposedPinchViewport } from './ComposedPinchViewport';
import { ComposedFitWidthFrame } from './ComposedFitWidthFrame';
import {
  getRenderTemplate,
  getComposerLiveData,
} from '../../../lib/report-composer';

// 결재란(approvalBlock)은 공유 ApprovalBlockSpecial로 렌더(admin EditorPage와 동일).
const COMPOSER_SPECIAL_COMPONENTS: SpecialComponentMap = {
  approvalBlock: ApprovalBlockSpecial,
};

/** 노드 1개(자신+자식)에 lifecycle==='live' 바인딩이 하나라도 있는지 재귀 검사. */
function nodeHasLiveBinding(node: ComposerNode): boolean {
  if (node.binding?.lifecycle === 'live') return true;
  switch (node.kind) {
    case 'group':
    case 'stack':
      return node.children.some(nodeHasLiveBinding);
    case 'repeat':
      return nodeHasLiveBinding(node.template);
    case 'branch':
      return node.branches.some((slot) => slot.children.some(nodeHasLiveBinding));
    case 'table':
      return node.cells.some(
        (cell) =>
          cell.binding?.lifecycle === 'live' ||
          (cell.content?.some(nodeHasLiveBinding) ?? false),
      );
    default:
      return false;
  }
}

/** 템플릿 body에 live lifecycle 바인딩이 하나라도 있으면 true(live fetch 필요 판정). */
function templateHasLiveBinding(template: ReportTemplate): boolean {
  return template.body.some(nodeHasLiveBinding);
}

export interface ComposedDocumentBodyProps {
  /** 생성 시 박제한 양식 스냅샷(StoredReportTemplate). 우선 사용(무결성). */
  templateSnapshot?: unknown;
  /** 스냅샷이 없을 때 live 로드할 양식 식별자. */
  templateKey?: string | null;
  /** 보고서 본문 데이터(생성 시 박제된 select-경로 평탄 객체). 계약상 unknown. */
  content: unknown;
  /** 결재란(approvalBlock special)이 읽는 결재 데이터. 미전달 시 결재란은 빈 상태. */
  approvalData?: ApprovalBlockData;
  /** 이미 확보한 live 데이터를 직접 주입(선택). reportId 내부 fetch보다 우선. */
  liveData?: Record<string, unknown>;
  /**
   * 상세 전용: 제공되면 live lifecycle 바인딩이 있을 때 내부에서 현재값을 fetch한다.
   * 공유/외부결재(무인증 경로)는 미제공 → live 바인딩은 undefined 렌더.
   */
  reportId?: string;
  /** live fetch 시 멀티테넌트 스코프. reportId와 함께 사용. */
  companyId?: string;
  /**
   * 값이 바뀌면 live 데이터를 강제로 재fetch한다(예: 현장 담당자 서명 수집 후 본문 서명란 즉시 반영).
   * live 바인딩은 reportId/template 변화가 없으면 재조회하지 않으므로, 외부 뮤테이션 후 이 토큰을 bump한다.
   */
  refreshToken?: number;
  /**
   * 모바일 핀치/팬 viewer 활성(opt-in, 기본 false).
   *
   * true + 비데스크탑일 때만 본문을 `ComposedPinchViewport`(fit scale + PinchViewerFrame)로 감싼다.
   * false면 정적 렌더(`ComposedFitWidthFrame` — 컨테이너보다 넓은 낱장만 축소, 세로 낱장은 종전과 동일)다.
   *
   * ⚠️ 현재 켜는 곳은 인증 상세(`ComposerReportDetail`) **한 곳뿐**이다. 공유(`/share/:token`)·
   * 외부결재(`/approval/:token`)는 주변 chrome(헤더/하단바 구성)이 달라 고정 높이 viewer의
   * `100dvh - top - 하단여백` 계산이 그대로 맞는지 별도 실측이 필요해 이번엔 켜지 않았다.
   * (PDF 캡처용 `composed-print-preview`는 이 컴포넌트를 쓰지 않아 영향 밖.)
   */
  pinchZoom?: boolean;
}

export function ComposedDocumentBody({
  templateSnapshot,
  templateKey,
  content,
  approvalData,
  liveData,
  reportId,
  companyId,
  refreshToken,
  pinchZoom = false,
}: ComposedDocumentBodyProps) {
  // 템플릿 로드: 박제된 스냅샷 우선, 없으면 templateKey 기준 live fetch.
  const [template, setTemplate] = useState<ReportTemplate | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);

  useEffect(() => {
    setTemplateError(null);
    // 스냅샷이 있으면 무결성을 위해 그것을 그대로 사용(live fetch 생략).
    if (templateSnapshot) {
      setTemplate(templateSnapshot as ReportTemplate);
      return;
    }
    if (!templateKey) {
      // 스냅샷도 key도 없으면 그릴 재료가 아예 없다. 예전엔 template=null로 조용히 빠져
      // 아래 `!template` 분기의 로딩 스피너가 **영원히** 돌았고(에러도 콘솔도 없음), 그 결과
      // "서버 응답 화이트리스트에 templateKey/templateSnapshot이 빠진" 진짜 원인을 화면만 보고는
      // 찾을 수 없었다(2026-07-28 삭제 승인 페이지). 조용한 무한 로딩 대신 명시적으로 실패를 알린다.
      setTemplate(null);
      setTemplateError('양식을 불러오지 못했습니다.');
      return;
    }
    let cancelled = false;
    setTemplate(null);
    getRenderTemplate(templateKey)
      .then((t) => {
        if (!cancelled) setTemplate(t);
      })
      .catch(() => {
        if (!cancelled) setTemplateError('양식을 불러오지 못했습니다.');
      });
    return () => {
      cancelled = true;
    };
  }, [templateKey, templateSnapshot]);

  // live(현재값) content: 상세(reportId 제공) + 템플릿에 live 바인딩이 있을 때만 fetch.
  // liveData prop이 직접 주입되면 그것을 쓰고 내부 fetch는 생략. fetch 실패는 graceful(snapshot 렌더).
  const [fetchedLiveData, setFetchedLiveData] = useState<
    Record<string, unknown> | undefined
  >(undefined);
  useEffect(() => {
    if (liveData !== undefined) return; // 직접 주입 우선
    if (!reportId || !template) return;
    // 서명 슬롯(P2)은 live 바인딩이 없어도 composer-live로만 값이 온다(__slotSignatures —
    // 걷은 서명은 content에 살지 않는 §3-2 설계). 슬롯 선언이 있으면 fetch 게이트를 연다.
    if (!templateHasLiveBinding(template) && !templateHasSignatureSlots(template)) {
      setFetchedLiveData(undefined);
      return;
    }
    let cancelled = false;
    getComposerLiveData(reportId, companyId)
      .then((c) => {
        if (!cancelled) setFetchedLiveData(c);
      })
      .catch((err) => {
        // live 조회 실패 시 snapshot으로만 렌더(본문이 깨지지 않도록 graceful).
        console.warn('[ComposedDocumentBody] live 데이터 조회 실패', err);
        if (!cancelled) setFetchedLiveData(undefined);
      });
    return () => {
      cancelled = true;
    };
    // refreshToken: 외부 뮤테이션(서명 수집 등) 후 강제 재fetch 트리거.
  }, [liveData, reportId, companyId, template, refreshToken]);

  const effectiveLiveData = liveData ?? fetchedLiveData;

  // RenderData 조립: resolveForRender(template, content, _, liveData)로 bindingKey 맵을 만든다.
  // approval(결재란 special 입력)은 산출 위에 키로 주입(approvalData 있을 때만).
  const renderData = useMemo<RenderData | null>(() => {
    if (!template) return null;
    // content는 select-경로 키 평탄 객체다. 비정상(배열/원시값/null)이면 {}로 정규화한다.
    const raw = content as unknown;
    const isPlainObject =
      typeof raw === 'object' && raw !== null && !Array.isArray(raw);
    const contentObj = (isPlainObject ? raw : {}) as Record<string, unknown>;
    return {
      ...resolveForRender(template, contentObj, undefined, effectiveLiveData),
      ...(approvalData ? { approval: approvalData } : {}),
    };
  }, [template, content, effectiveLiveData, approvalData]);

  // 낱장 수 — 핀치 viewer의 높이 재측정 트리거(remeasureKey)로만 쓴다. rc `PagedComposerView`가
  // 배치 안정화 시 통지하는 기존 prop(onPageCount)을 소비할 뿐 rc는 손대지 않는다.
  // pinchZoom이 꺼진 surface에는 콜백 자체를 넘기지 않아 렌더 횟수까지 종전과 같다.
  const [pageCount, setPageCount] = useState(0);

  if (templateError) {
    return (
      <div className="rounded-card bg-red-50 p-4">
        <p className="text-sm text-red-700">{templateError}</p>
      </div>
    );
  }

  if (!template || !renderData) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const pages = (
    <PagedComposerView
      template={template}
      data={renderData}
      specialComponents={COMPOSER_SPECIAL_COMPONENTS}
      onPageCount={pinchZoom ? setPageCount : undefined}
    />
  );

  // 낱장 폭 — 컨테이너보다 넓은 가로(landscape) 낱장은 두 경로 모두 축소 fit으로 그린다.
  // 종전 `overflow-x-auto`만으로는 flex 중앙 정렬된 낱장이 **양쪽**으로 넘쳐 왼쪽이 스크롤로도
  // 닿지 않았다(2026-09-14 위험성평가 결과서 실측). 세로 낱장은 scale 1이라 종전과 같다.
  const pageWidthPx = pagePx(template.page).w;

  // opt-in이 아니면(공유·외부결재) 정적 fit 프레임. opt-in이면 모바일에서만 핀치 viewer로 감싼다
  // (데스크탑 분기는 ComposedPinchViewport 안에서 같은 fit 프레임으로 되돌아간다).
  if (!pinchZoom) {
    return <ComposedFitWidthFrame pageWidthPx={pageWidthPx}>{pages}</ComposedFitWidthFrame>;
  }

  return (
    <ComposedPinchViewport pageWidthPx={pageWidthPx} pageCount={pageCount}>
      {pages}
    </ComposedPinchViewport>
  );
}
